<script lang="ts" setup>
import BeefreeSDK from '@beefree.io/sdk';
import type { IBeeConfig, IEntityContentJson, IToken } from '@beefree.io/sdk/dist/types/bee';
import { merge } from 'lodash-es';
import type { BaseModel, SubscriberList, User } from 'types';
import type { Ref } from 'vue';
import { computed, onMounted, onUnmounted, ref, toRaw } from 'vue';
import { useBeeMcpSession } from '../composables/useBeeMcpSession';

const props = withDefaults(defineProps<{
    token: IToken;
    modelValue?: Partial<IEntityContentJson>;
    list: SubscriberList;
    authUser: User;
    config?: Partial<IBeeConfig>,
    model: BaseModel;
}>(), {
    config: undefined,
    modelValue: () => ({})
});

const emit = defineEmits<{
    change: [template: IEntityContentJson],
    load: [template: IEntityContentJson],
    save: [template: IEntityContentJson, html: string],
    'update:modelValue': [newValue: IEntityContentJson]
}>();

const bee = new BeefreeSDK(toRaw(props.token));

const template = ref<IEntityContentJson>();

// Must equal the uid the auth token was generated with (see BeeService::token()).
const beeUid = `lid${props.list.id}`;
const beeMcpSession = useBeeMcpSession();

const customFonts = computed(() => {
    if(!props.list || !props.list.options.custom_fonts) {
        return;
    }

    const nonNullableFonts = props.list.options.custom_fonts.filter(({ url, font_family }: { url: string | null; font_family: string | null }) => {
        return url && font_family;
    }) as {
        url: string,
        font_family: string
    }[];

    const googleFontWeights:Record<number,string> = {
        100: 'Thin',
        200: 'Extra-light',
        300: 'Light',
        400: 'Regular',
        500: 'Medium',
        600: 'Semi-bold',
        700: 'Bold',
        800: 'Extra-bold',
        900: 'Black'
    };
        
    return nonNullableFonts.map(font => {
        const params = (new URL(font.url)).searchParams;
        const family = params.get('family');
        const weight = Array.from(family?.match(/[100-900][0]+/g) ?? []);
         
        return {
            name: family?.split(':')[0] ?? font.font_family.split(',')[0].match(/(?<quote>'|")(\w+)\1/)?.[2],
            fontFamily: font.font_family,
            url: font.url,
            fontWeight: weight.map(value => parseInt(value))
                .reduce<Record<number,string>>((carry, value) => {
                    if(value in googleFontWeights) {
                        carry[value] = googleFontWeights[value];
                    }

                    return carry;
                }, {})
        };
    });
});

const beeConfig: Ref<IBeeConfig> = computed(() => merge({
    //uid allows us to segment images by list on the s3 bucket, original uid: 9FO4rOJlVo2H
    uid: beeUid,
    container: 'bee-plugin-container',
    roleHash: props.authUser.super_admin ? 'administrator' : undefined,
    trackChanges: true,
    onLoad(value: IEntityContentJson) {
        beeMcpSession.setSnapshot(JSON.stringify(value).slice(0, 4000));
        emit('load', value);
    },
    onChange(value: string) {
        beeMcpSession.setSnapshot(value.slice(0, 4000));
        emit('update:modelValue', JSON.parse(value) as IEntityContentJson);
    },
    onSave: (value: string, html: string) => {
        template.value = JSON.parse(value) as IEntityContentJson;

        emit('save', template.value, html);
    },
    onError: (error: unknown) => {
        console.error(error);
    },
    onWarning: (error: unknown) => {
        console.warn(error);
    },
    editorFonts: {
        showDefaultFonts: true,
        customFonts: customFonts.value
    },
}, props.config));

async function start(template?: Partial<IEntityContentJson>) {
    await bee.start(toRaw(beeConfig.value), toRaw(template) as IEntityContentJson ?? {});
}

async function init() {
    start(props.modelValue);
};

onMounted(() => {
    beeMcpSession.register(() => bee.startMcpSession());
    init();
});

onUnmounted(() => {
    beeMcpSession.clear();
});

defineExpose({
    bee,
    start,
    template,
    beeUid,
    toggleMergeTagsPreview: () => bee.toggleMergeTagsPreview()
});

//sm:scale-[.60] md:scale-[.80] lg:scale-[.95] xl:scale-100
</script>
<template>
    <div
        id="bee-plugin-container"
        class="h-[900px] w-full overflow-x-auto" />
</template>